import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { orderMenuPosition } from "./order-menu-position";
import "./mobile-order-actions.scss";
const OPEN_MENU = "easybar-open-order-menu";
export function AnchoredOrderMenu({ anchor, rtl, label, onClose, children }: { anchor: HTMLElement | null; rtl: boolean; label: string; onClose: () => void; children: React.ReactNode }) {
  const menu = useRef<HTMLDivElement>(null);
  const pressingInside = useRef(false);
  const close = useRef(onClose); close.current = onClose;
  const location = useLocation();
  const initialLocation = useRef(location);
  const [position, setPosition] = useState<ReturnType<typeof orderMenuPosition>>();
  useLayoutEffect(() => {
    if (!anchor || !menu.current) return;
    const viewport = window.visualViewport;
    setPosition(orderMenuPosition(anchor.getBoundingClientRect(), { left: viewport?.offsetLeft || 0, top: viewport?.offsetTop || 0, width: viewport?.width || window.innerWidth, height: viewport?.height || window.innerHeight }, menu.current.scrollHeight, rtl));
  }, [anchor, rtl]);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(OPEN_MENU, { detail: anchor }));
    const other = (event: Event) => { if ((event as CustomEvent).detail !== anchor) close.current(); };
    const outside = (event: Event) => { if (!menu.current?.contains(event.target as Node) && !anchor?.contains(event.target as Node)) close.current(); };
    const scroll = (event: Event) => { if (pressingInside.current) return; if (!(event.target instanceof Node) || !menu.current?.contains(event.target)) close.current(); };
    const resize = () => { if (!pressingInside.current) close.current(); };
    const release = () => { releaseTimer = window.setTimeout(() => { pressingInside.current = false; }, 0); };
    const cancel = () => { pressingInside.current = false; };
    let releaseTimer: number | undefined;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); close.current(); anchor?.focus({ preventScroll: true }); }
    };
    window.addEventListener(OPEN_MENU, other);
    document.addEventListener("keydown", escape);
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("pointerup", release);
    document.addEventListener("pointercancel", cancel);
    document.addEventListener("click", outside, true);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("scroll", resize);
    const frame = requestAnimationFrame(() => menu.current?.querySelector<HTMLElement>('button:not(:disabled), a')?.focus({ preventScroll: true }));
    return () => {
      cancelAnimationFrame(frame); window.clearTimeout(releaseTimer);
      document.removeEventListener("pointerup", release); document.removeEventListener("pointercancel", cancel);
      window.removeEventListener(OPEN_MENU, other);
      document.removeEventListener("keydown", escape);
      document.removeEventListener("pointerdown", outside, true); document.removeEventListener("click", outside, true);
      window.removeEventListener("scroll", scroll, true); window.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("resize", resize); window.visualViewport?.removeEventListener("scroll", resize);
    };
  }, [anchor]);
  useEffect(() => { if (location !== initialLocation.current) close.current(); }, [location]);
  return createPortal(<div ref={menu} className="mobile-order-actions-menu" role="menu" aria-label={label} dir={rtl ? "rtl" : "ltr"}
    style={{ position: "fixed", ...position, visibility: position ? "visible" : "hidden" }}
    onPointerDownCapture={() => { pressingInside.current = true; }}
    onClick={event => { pressingInside.current = false; event.stopPropagation(); if ((event.target as HTMLElement).closest("button:not(:disabled), a")) close.current(); }}
    onKeyDown={event => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close.current(); anchor?.focus({ preventScroll: true }); }
      const items = Array.from(menu.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a') || []);
      const index = items.indexOf(document.activeElement as HTMLElement);
      const next = event.key === "ArrowDown" ? (index + 1) % items.length : event.key === "ArrowUp" ? (index - 1 + items.length) % items.length : event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : -1;
      if (next >= 0) { event.preventDefault(); items[next]?.focus(); }
    }}>{children}</div>, document.body);
}
export function ResponsiveOrderMenu({ mobile, children, ...props }: React.ComponentProps<typeof AnchoredOrderMenu> & { mobile: boolean }) {
  return mobile ? <AnchoredOrderMenu {...props}>{children}</AnchoredOrderMenu> : <div className="supplier-order-actions-menu" onClick={event => event.stopPropagation()}>{children}</div>;
}
